import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react'
import {
  Box,
  IconButton,
  Avatar,
  TextField,
  ListItemText,
  Typography,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Tooltip,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  DeleteOutline,
  ArrowDropDownOutlined,
  MoreVertOutlined,
  AddCircleOutlineOutlined,
  ViewSidebarOutlined,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import UserMenu from './UserMenu'
import * as memoryService from '../../services/memoryService'
import { NAV_ITEMS } from '../../constants/navlinks'
import { ARCANA_ENABLED, PROJECTS_ENABLED } from '../../constants/featureFlags'
import { useChatStore } from '../../stores/useChatStore'
import { useUIStore } from '../../stores/useUIStore'
import { useAuthContext } from '../../contexts/AuthContext'
import { useConversationManagement } from '../../hooks/useConversationManagement'
import { useChatActions } from '../../hooks/useChatActions'
import { useShallow } from 'zustand/react/shallow'
import { submitFeedback } from '../../services/feedbackService'
import SettingsModal from '../../pages/ChatPage/SettingsModal'
import FeedbackModal from '../../pages/ChatPage/FeedbackModal'
import DeleteConfirmDialog from '../../pages/ChatPage/DeleteConfirmModal'
import {
  Container,
  Header,
  NavList,
  NavListItem,
  NewChatButton,
  ProfileWrapper,
  SidebarContent,
  SidebarDrawer,
} from './Sidebar.styles'
import ChatHistoryList from '../ChatPage/ChatHistoryList/ChatHistoryList'
import MemoryList from '../ChatPage/MemoryList/MemoryList'

const ArcanoListLazy = ARCANA_ENABLED
  ? lazy(() => import('../ChatPage/ArcanoList/ArcanoList'))
  : () => null

const ProjectListLazy = PROJECTS_ENABLED
  ? lazy(() => import('../ChatPage/ProjectList/ProjectList'))
  : () => null

const RESTRICTED_FEATURES = [
  'image_generation',
  'video_generation',
  'cad_generation',
  'presentation_generation',
]

const Sidebar = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { user, token, fetchUser } = useAuthContext()
  const [activeSection, setActiveSection] = useState(null)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [selectedConv, setSelectedConv] = useState(null)
  const [expanded, setExpanded] = useState(false)
  const [memories, setMemories] = useState([])
  const [isLoadingMemories, setIsLoadingMemories] = useState(false)

  const { handleClearArcano, handleClearFilters } = useConversationManagement()
  const { handleNewConversation } = useChatActions()
  const { searchQuery, setSearchQuery } = useChatStore(
    useShallow(state => ({
      searchQuery: state.searchQuery,
      setSearchQuery: state.setSearchQuery,
    }))
  )
  const {
    isSidebarOpen,
    setIsSidebarOpen,
    setIsUserMenuOpen,
    openDeleteConfirm,
    toggleSidebar,
    isSettingsModalOpen,
    isFeedbackModalOpen,
    setIsSettingsModalOpen,
    setIsFeedbackModalOpen,
    showDeleteConfirm,
    showDeleteAllConfirm,
    conversationToDelete,
    closeDeleteConfirm,
    closeDeleteAllConfirm,
  } = useUIStore(
    useShallow(state => ({
      isSidebarOpen: state.isSidebarOpen,
      setIsSidebarOpen: state.setIsSidebarOpen,
      setIsUserMenuOpen: state.setIsUserMenuOpen,
      openDeleteConfirm: state.openDeleteConfirm,
      toggleSidebar: state.toggleSidebar,
      isSettingsModalOpen: state.isSettingsModalOpen,
      isFeedbackModalOpen: state.isFeedbackModalOpen,
      setIsSettingsModalOpen: state.setIsSettingsModalOpen,
      setIsFeedbackModalOpen: state.setIsFeedbackModalOpen,
      showDeleteConfirm: state.showDeleteConfirm,
      showDeleteAllConfirm: state.showDeleteAllConfirm,
      conversationToDelete: state.conversationToDelete,
      closeDeleteConfirm: state.closeDeleteConfirm,
      closeDeleteAllConfirm: state.closeDeleteAllConfirm,
    }))
  )

  const { handleDeleteConversation, handleDeleteAllConversations } = useChatActions(
    useShallow(state => ({
      handleDeleteConversation: state.handleDeleteConversation,
      handleDeleteAllConversations: state.handleDeleteAllConversations,
    }))
  )

  useEffect(() => {
    handleClearArcano()
    setActiveSection('home')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSettingsSave = async () => {
    if (token) {
      await fetchUser(token)
    }
  }

  const handleSubmitFeedback = async message => {
    await submitFeedback(token, message)
  }

  const handleMenuOpen = (event, conversation) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setSelectedConv(conversation)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
    setSelectedConv(null)
  }

  const isNavItemActive = key => {
    return key === activeSection
  }

  const handleDelete = () => {
    if (selectedConv) {
      openDeleteConfirm(selectedConv)
    }
    handleMenuClose()
  }

  const fetchMemories = useCallback(async () => {
    if (!token) return

    setIsLoadingMemories(true)
    try {
      const response = await memoryService.getMemories(token, {
        limit: 10,
        skip: 0,
        status_filter: 'active',
      })
      setMemories(response.items || [])
    } catch (err) {
      console.error('Error fetching memories:', err)
      setMemories([])
    } finally {
      setIsLoadingMemories(false)
    }
  }, [token])

  useEffect(() => {
    fetchMemories()
  }, [fetchMemories])

  const hasMemories = memories && memories.length > 0

  const handleOpenSection = sectionKey => {
    if (!isSidebarOpen) {
      setIsSidebarOpen(true)
    }

    const navItem = NAV_ITEMS.find(item => item.key === sectionKey)
    if (!navItem) return

    setExpanded(false)

    if (sectionKey === 'home') {
      navigate('/chat')
      handleNewConversation()
      handleClearFilters()
      setActiveSection('home')
      return
    }

    if (sectionKey === activeSection) {
      return
    }

    if (sectionKey === 'memories') {
      if (hasMemories) {
        setActiveSection(sectionKey)
      } else {
        navigate('/memories')
        setActiveSection(null)
      }
      return
    }

    if (sectionKey === 'chat_assets') {
      navigate('/chat/assets')
      setActiveSection(sectionKey)
      return
    }

    if (
      RESTRICTED_FEATURES.includes(sectionKey) &&
      user.feature_flags.includes(sectionKey)
    ) {
      navigate(navItem.route)
      setActiveSection(sectionKey)
    } else {
      setActiveSection(sectionKey)
    }
  }

  const renderSectionContent = itemKey => {
    if (!isSidebarOpen) return null

    switch (itemKey) {
      case 'home':
        return <ChatHistoryList handleMenuOpen={handleMenuOpen} />
      case 'arcano':
        if (!ARCANA_ENABLED) return null
        return (
          <Suspense fallback={null}>
            <ArcanoListLazy
              expanded={expanded}
              setExpanded={setExpanded}
              handleMenuOpen={handleMenuOpen}
            />
          </Suspense>
        )
      case 'projects':
        if (!PROJECTS_ENABLED) return null
        return (
          <Suspense fallback={null}>
            <ProjectListLazy
              activeSection={activeSection}
              handleMenuOpen={handleMenuOpen}
            />
          </Suspense>
        )
      case 'memories':
        return (
          <MemoryList
            memories={memories}
            isLoadingMemories={isLoadingMemories}
            navigate={navigate}
          />
        )
      default:
        return null
    }
  }

  const processedNavItems = useMemo(() => {
    return NAV_ITEMS.filter(item => {
      const isRestricted = RESTRICTED_FEATURES.includes(item.key)
      const isAllowed = user.feature_flags.includes(item.key)
      return !isRestricted || isAllowed
    }).map(item => ({
      ...item,
      isDropdown:
        (item.key === 'memories' ? hasMemories : item.isDropdown) && isSidebarOpen,
    }))
  }, [user.feature_flags, hasMemories, isSidebarOpen])

  const handleNewChat = () => {
    if (!isSidebarOpen) {
      setIsSidebarOpen(true)
    }
    handleNewConversation()
    handleClearFilters()
    setActiveSection('home')
    setExpanded(false)
  }

  return (
    <Container>
      <SidebarDrawer variant="permanent" ownerState={{ isSidebarOpen }}>
        <SidebarContent ownerState={{ isSidebarOpen }}>
          <Header ownerState={{ isSidebarOpen }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img
                src="/favicon.ico"
                alt="logo"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>

            <Tooltip
              title={isSidebarOpen ? 'Chiudi Sidebar' : 'Apri Sidebar'}
              placement="right"
            >
              {isSidebarOpen && (
                <IconButton
                  onClick={toggleSidebar}
                  sx={{
                    mr: isSidebarOpen ? 1 : 0,
                    transition: 'transform 0.2s',
                    transform: isSidebarOpen ? 'scaleX(1)' : 'scaleX(-1)',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ViewSidebarOutlined />
                </IconButton>
              )}
            </Tooltip>
          </Header>
          <NewChatButton
            variant="contained"
            startIcon={<AddCircleOutlineOutlined />}
            onClick={handleNewChat}
            ownerState={{ isSidebarOpen }}
          >
            {isSidebarOpen && 'Nuova Chat'}
          </NewChatButton>

          <Divider sx={{ mb: 2 }} />

          {isSidebarOpen && (
            <TextField
              placeholder="Cerca tra le chat..."
              size="small"
              variant="outlined"
              fullWidth
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              sx={{
                mb: 1,
                mt: 0,
                borderRadius: 10,
                bgcolor: 'background.paper',
              }}
            />
          )}

          <NavList>
            {processedNavItems.map(item => {
              const Icon = item.icon

              return (
                <React.Fragment key={item.key}>
                  <Tooltip
                    title={!isSidebarOpen ? item.name : ''}
                    placement="right"
                    arrow
                  >
                    <NavListItem
                      onClick={() => handleOpenSection(item.key)}
                      isActive={isNavItemActive(item.key)}
                      sx={{
                        justifyContent: isSidebarOpen ? 'flex-start' : 'center',
                        pr: isSidebarOpen ? 1.5 : 0,
                        pl: isSidebarOpen ? 1 : 0,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          justifyContent: 'center',
                          color: isNavItemActive(item.key)
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                          mr: isSidebarOpen ? 1 : 0,
                        }}
                      >
                        <Icon fontSize="small" />
                      </ListItemIcon>
                      {isSidebarOpen && <ListItemText primary={item.name} />}
                      {item.isDropdown && <ArrowDropDownOutlined fontSize="small" />}
                    </NavListItem>
                  </Tooltip>

                  {activeSection === item.key && renderSectionContent(item.key)}
                </React.Fragment>
              )
            })}
          </NavList>
        </SidebarContent>

        <ProfileWrapper
          onClick={e => setIsUserMenuOpen(e.currentTarget)}
          ownerState={{ isSidebarOpen }}
        >
          <Avatar
            src={user.profile_picture || '/default-avatar.png'}
            alt="User Profile"
            sx={{
              width: 32,
              height: 32,
              mr: isSidebarOpen ? 1.5 : 0,
              ml: isSidebarOpen ? 0 : 0.5,
            }}
          />

          {isSidebarOpen && (
            <>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap fontWeight="bold">
                  {user.display_name || 'Utente'}
                </Typography>
              </Box>
              <MoreVertOutlined fontSize="small" sx={{ marginRight: 2 }} />
            </>
          )}
        </ProfileWrapper>

        <UserMenu />

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={handleDelete}
            sx={{
              color: 'error.main',
            }}
          >
            <DeleteOutline fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">Elimina</Typography>
          </MenuItem>
        </Menu>
      </SidebarDrawer>

      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        onSave={handleSettingsSave}
      />

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        onSubmit={handleSubmitFeedback}
      />

      <DeleteConfirmDialog
        open={showDeleteConfirm}
        conversation={conversationToDelete}
        onClose={closeDeleteConfirm}
        onConfirm={handleDeleteConversation}
        type="single"
      />

      <DeleteConfirmDialog
        open={showDeleteAllConfirm}
        onClose={closeDeleteAllConfirm}
        onConfirm={handleDeleteAllConversations}
        type="all"
      />
    </Container>
  )
}

export default Sidebar
